import customTheme from '@/constants/theme.custom';
import envService from '@/services/env.service';
import { useTheme } from '@cmpsr/components';
import { StripeElementsOptionsMode, loadStripe } from '@stripe/stripe-js';
import { VARS } from '@/app/components/ppra/components/common/constants';

const stripePromise = loadStripe(envService.getEnv().VITE_STRIPE_PUBLISHABLE_KEY);

type PaymentVariant = 'default' | 'dark' | 'transparent';

interface UsePaymentControllerOptions {
  variant?: PaymentVariant;
  amount?: number;
  isTrial?: boolean;
}

export default function usePaymentController(
  options: UsePaymentControllerOptions = {}
) {
  const { variant = 'default', amount, isTrial = false } = options;
  const theme = useTheme();

  const getAppearance = (): StripeElementsOptionsMode['appearance'] => {
    const baseAppearance = {
      theme: 'stripe' as const,
      variables: {
        // Font Family
        fontFamily: 'Inter',
        fontWeightNormal: '400',
        fontWeightMedium: '500',
        fontWeightBold: '700',
        fontSizeBase: '16px',
        fontLineHeight: '1.5',

        // Colors
        colorPrimary: theme.colors['primary-default'],
        colorBackground: theme.colors['background-container-default'],
        colorText: theme.colors['text-primary'],
        colorDanger: customTheme.colors['salmon-light'],
        colorSuccess: theme.colors['alert-success-default'],
        colorWarning: theme.colors['alert-warning-default'],
        colorTextSecondary: theme.colors['text-secondary'],
        colorTextPlaceholder: theme.colors['text-disabled'],

        // Accessible text colors
        accessibleColorOnColorPrimary: theme.colors['text-button-primary'],
        accessibleColorOnColorBackground: theme.colors['text-primary'],
        accessibleColorOnColorSuccess: theme.colors['text-success'],
        accessibleColorOnColorDanger: theme.colors['text-error'],
        accessibleColorOnColorWarning: theme.colors['text-warning'],

        // Spacing and borders
        spacingUnit: '4px',
        borderRadius: theme.radii['radii-2'],

        // Icons
        iconColor: theme.colors['text-primary'],
        iconHoverColor: theme.colors['primary-hover'],
        iconCardErrorColor: theme.colors['alert-error-default'],
        iconCardCvcColor: theme.colors['text-secondary'],
        iconCardCvcErrorColor: theme.colors['alert-error-default'],
        iconCheckmarkColor: theme.colors['alert-success-default'],
        iconChevronDownColor: theme.colors['text-secondary'],
        iconChevronDownHoverColor: theme.colors['primary-hover'],
        iconCloseColor: theme.colors['text-secondary'],
        iconCloseHoverColor: theme.colors['primary-hover'],
        iconLoadingIndicatorColor: theme.colors['secondary-default'],
        iconMenuColor: theme.colors['text-secondary'],
        iconMenuHoverColor: theme.colors['primary-hover'],
        iconPasscodeDeviceColor: theme.colors['text-secondary'],
        iconPasscodeDeviceHoverColor: theme.colors['primary-hover'],
        iconPasscodeDeviceNotificationColor: theme.colors['alert-error-default'],
        iconRedirectColor: theme.colors['text-secondary'],
        tabIconColor: theme.colors['text-secondary'],
        tabIconHoverColor: theme.colors['primary-hover'],
        tabIconSelectedColor: theme.colors['primary-default'],
        tabIconMoreColor: theme.colors['text-secondary'],
        tabIconMoreHoverColor: theme.colors['primary-hover'],

        // Logo colors
        logoColor: 'dark',
        tabLogoColor: 'dark',
        tabLogoSelectedColor: 'dark',
        blockLogoColor: 'dark',
      },
    };

    // https://docs.stripe.com/elements/appearance-api
    switch (variant) {
      case 'transparent':
        return {
          ...baseAppearance,
          variables: {
            ...baseAppearance.variables,
            colorText: '#FFFFFF',
            colorBackground: '#FFFFFF',
            colorPrimary: '#FFFFFF',
            colorTextSecondary: '#FFFFFF',
            colorTextPlaceholder: '#FFFFFF',
            colorSuccess: '#4CAF50',
            colorWarning: customTheme.colors['salmon-light'],
            iconColor: '#000000',
            iconHoverColor: '#000000',
            iconCardErrorColor: customTheme.colors['salmon-light'],
            iconCardCvcColor: '#E6E6E6',
            iconCardCvcErrorColor: customTheme.colors['salmon-light'],
            iconCheckmarkColor: '#4CAF50',
            iconChevronDownColor: '#E6E6E6',
            iconChevronDownHoverColor: '#FF6B6B',
            iconCloseColor: '#E6E6E6',
            iconCloseHoverColor: '#FF6B6B',
            iconLoadingIndicatorColor: '#FF6B6B',
            iconMenuColor: '#E6E6E6',
            iconMenuHoverColor: '#FF6B6B',
            iconPasscodeDeviceColor: '#E6E6E6',
            iconPasscodeDeviceHoverColor: '#FF6B6B',
            iconPasscodeDeviceNotificationColor: customTheme.colors['salmon-light'],
            iconRedirectColor: '#FFFFFF',
            tabIconColor: '#000000',
            tabIconHoverColor: '#000000',
            tabIconSelectedColor: '#000000',
            tabIconMoreColor: '#000000',
            tabIconMoreHoverColor: '#000000',
          },
          rules: {
            '.Tab': {
              backgroundColor: 'transparent',
              border: 'none',
            },
            '.Tab--selected': {
              backgroundColor: 'transparent',
              border: 'none',
            },
            '.Input': {
              backgroundColor: 'transparent',
              color: customTheme.colors.white,
              border: `1px solid ${customTheme.colors.white}`,
              borderRadius: theme.radii['radii-4'],
              padding: '0.75rem',
            },
            '.Input:focus': {
              outline: `0 0 0 0.1875rem ${customTheme.colors['salmon-light']}`,
            },
            '.Input::placeholder': {
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '14px',
            },
            '.Input:autofill': {
              backgroundColor: 'transparent',
            },
            '.Label': {
              color: customTheme.colors.white,
              fontFamily: 'Inter',
              fontSize: '10px',
              fontWeight: '400',
              letterSpacing: '1px',
              textTransform: 'uppercase',
            },
            '.AccordionItem': {
              border: 'none',
              color: 'rgba(255, 255, 255, 0.6)',
              backgroundColor: 'transparent',
              fontWeight: '500',
              boxShadow: 'none',
              padding: '0 0 16px 0',
            },
            '.AccordionItem--selected': {
              color: customTheme.colors.white,
            },
            '.Block': {
              backgroundColor: 'transparent',
            },
          },
        };
      case 'dark':
        return {
          ...baseAppearance,
          variables: {
            ...baseAppearance.variables,
            colorText: customTheme.colors.white,
            colorBackground: customTheme.colors.darkBlue,
          },
          rules: {
            '.Tab': {
              border: 'none',
              backgroundColor: customTheme.colors.darkBlue,
            },
            '.Tab--selected': {
              border: 'none',
              backgroundColor: customTheme.colors.darkBlue,
            },
            '.Input': {
              backgroundColor: 'transparent',
              color: customTheme.colors.white,
              border: `1px solid ${customTheme.colors.white}`,
              borderRadius: theme.radii['radii-2'],
              padding: '0.75rem',
            },
            '.Input:focus': {
              outline: `0 0 0 0.1875rem ${customTheme.colors['salmon-light']}`,
            },
            '.Input::placeholder': {
              color: 'rgba(255, 255, 255, 0.6)',
            },
            '.Input:autofill': {
              backgroundColor: 'transparent',
            },
            '.Label': {
              color: customTheme.colors.white,
              textTransform: 'uppercase',
              fontSize: '0.75rem',
              marginBottom: '0.500rem',
            },
            '.AccordionItem': {
              border: 'none',
              color: customTheme.colors.white,
              fontWeight: '500',
            },
            '.AccordionItem--selected': {
              color: customTheme.colors.white,
            },
          },
        };
      case 'default':
        return {
          ...baseAppearance,
          variables: {
            ...baseAppearance.variables,
          },
          rules: {
            '.AccordionItem': {
              color: theme.colors['text-primary'],
              fontWeight: '500',
              paddingTop: '0px',
              paddingBottom: '12px',
              paddingLeft: '0',
              paddingRight: '0',
              border: 'none',
            },
            '.AccordionItem--selected': {
              color: theme.colors['text-primary'],
            },
            '.Label': {
              color: theme.colors['text-primary'],
              fontFamily: 'Inter',
              fontSize: '10px',
              fontWeight: '400',
              letterSpacing: '1px',
              textTransform: 'uppercase',
            },
            '.Block': {
              borderColor: theme.colors['ui-element-outline-default'],
            },
            '.Input': {
              border: `1px solid ${theme.colors['ui-element-outline-default']}`,
              borderRadius: theme.radii['radii-2'],
              marginBottom: '8px',
            },
            '.Input:focus': {
              outline: '0 0 0 0.1875rem var(--chakra-colors-primary-focus)',
            },
            '.Input::placeholder': {
              color: theme.colors['text-disabled'],
            },
            '.Input:autofill': {
              backgroundColor: 'transparent',
            },
          },
        };
    }
  };

  const appearance = getAppearance();

  const stripeOptions: StripeElementsOptionsMode = isTrial
    ? {
        // Payment mode for trials with $1 verification hold
        // This allows stripe.confirmPayment() to work with the verification PaymentIntent
        fonts: [
          {
            cssSrc: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500',
          },
        ],
        mode: 'payment',
        currency: 'usd',
        amount: 100, // $1.00 for card verification
        captureMethod: 'manual', // Must match backend PaymentIntent capture_method
        setupFutureUsage: 'off_session', // Must match backend - attaches payment method to customer
        paymentMethodTypes: ['card'],
        locale: 'en',
        appearance,
        loader: 'always',
      }
    : {
        // Subscription mode for regular payments
        fonts: [
          {
            cssSrc: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500',
          },
        ],
        mode: 'subscription',
        currency: 'usd',
        amount: amount || VARS.subscription.defaultAmount,
        paymentMethodTypes: ['card'],
        locale: 'en',
        appearance,
        loader: 'always',
      };

  return { stripePromise, stripeOptions };
}
